import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useForm, useFormState } from 'react-final-form'
import { useFieldArray } from 'react-final-form-arrays'
import isEqual from 'lodash/isEqual'

import ticketFilterVisibility from 'constants/ticketFilterVisibility'
import { buildQuery } from 'utils/filterQuery'
import { usePrevious } from 'utils/hooks'

import colors from 'components/utils/colors'
import TextAreaField from 'components/Shared/TableFormFieldsBuilder/Fields/TextAreaField'
import SwitchField from 'components/Shared/TableFormFieldsBuilder/Fields/SwitchField'
import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableCellWithActions from 'components/Shared/TableNew/TableCellWithActions'
import TableCellAction from 'components/Shared/TableNew/TableCellAction'
import { useFilterFields } from './hooks'
import ColumnSelectField from './ColumnSelectField'
import OperatorSelectField from './OperatorSelectField'
import ValueField from './ValueField'

const WIDTH = 842
const WIDTH_FIELD_CELL = 280
const WIDTH_OPERATOR_CELL = 186
const WIDTH_VALUE_CELL = 336
const WIDTH_LAST_CELL = WIDTH - (WIDTH_FIELD_CELL + WIDTH_OPERATOR_CELL + WIDTH_VALUE_CELL)

const StyledWrapper = styled.div`
`

const GroupHeader = styled.div`
  padding-top: 44px;
  padding-bottom: 16px;
  font-family: Exo;
  font-size: 20px;
  line-height: 26px;
  color: ${colors.secondary20};
`

const TopButtons = styled.div`
  display: flex;
  flex-flow: row-reverse;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 28px;
  min-height: 16px;
`

const AdvancedSwitcherWithInfo = styled.div`
  display: flex;
  gap: 8px;
`

const ModePanel = styled.div`
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  min-height: 274px;
`

const NormalModePanel = styled(ModePanel)`
`

const AdvancedModePanel = styled(ModePanel)`
`

const QueryWrapper = styled.div`
  background: ${colors.secondary95};
  padding: 8px 14px 8px 8px;
  min-width: ${WIDTH}px;
  box-sizing: border-box;
`

const ResultExpression = styled.div`
  max-width: ${WIDTH}px;
  font-size: 14px;
  color: ${colors.secondary40};
  line-height: 16px;
  text-align: justify;
`

const emptyCondition = {
  column: '',
  equality: '',
  value: '',
}

const ConditionsSection = ({ ticketProviderId, partnerStatuses }) => {
  const { t } = useTranslation()
  const conditions = useFieldArray('conditions', {
    isEqual: isEqual,
    // Typing in input fields is quite slow
    // See https://github.com/final-form/react-final-form-arrays/issues/119
    // It was recommended to remove all subsctriptions
    // (we usually use { invalid: true, pristine: true, value: true })
    // and use conditionsValues instead of conditions.fields.value
    // But it doesn't look that it helped
    subscription: {},
  })
  const form = useForm()
  const formState = useFormState()
  const formValues = formState.values
  const { advanced, visibility, conditions: conditionsValues } = formValues
  const prevAdvanced = usePrevious(advanced)

  const query = buildQuery(conditionsValues) || ''
  const isGlobal = visibility !== ticketFilterVisibility.PRIVATE

  const { filterFields, filterFieldsMap, isFetching } = useFilterFields({
    ticketProviderId: ticketProviderId,
    isGlobal: isGlobal,
    partnerStatuses: partnerStatuses,
  })

  // on isGlobal change remove rows where column names are not in the new filterFields
  useEffect(() => {
    if (filterFields.length === 0) {
      return
    }

    const removingIndexes = []
    conditions.fields.forEach((name, index) => {
      const condition = conditionsValues[index]
      const columnName = condition?.column || null

      if (columnName && !filterFieldsMap[columnName]) {
        removingIndexes.push(index)
      }
    })

    removeConditionsAsync(removingIndexes)
  }, [isGlobal, filterFields]) // eslint-disable-line react-hooks/exhaustive-deps

  // on switching to advanced copy query there
  useEffect(() => {
    if (advanced && prevAdvanced === false) {
      // without setTimeout it doesn't revalidate probably due to this (last question):
      // https://final-form.org/docs/react-final-form/faq
      setTimeout(() => {
        form.change('query', query)
      })
    }
    if (!advanced && prevAdvanced) {
      form.change('query', '')
    }
  }, [advanced]) // eslint-disable-line react-hooks/exhaustive-deps

  const removeConditionsAsync = (indexes) => {
    const keepLastCondition = indexes.length === conditions.fields.length
    if (keepLastCondition) {
      indexes.shift()
      conditions.fields.update(0, emptyCondition)
    }
    conditions.fields.removeBatch(indexes)
  }

  const onConditionAdd = () => {
    conditions.fields.push(emptyCondition)
  }

  const onConditionDelete = (index) => {
    removeConditionsAsync([index])
  }

  // Clear value on manual change of column to prevent adding
  // previous value select options to current value select.
  // It also clears text inputs but it's ok for now.
  const onColumnChange = (index, value) => {
    conditions.fields.update(index, {
      column: conditionsValues[index].column,
      equality: conditionsValues[index].equality,
      value: '',
    })
  }

  const hideDeleteButton = conditions.fields.length === 1 && isEqual(conditionsValues, [emptyCondition])
  const resultExpression = query ? t('ticketProviderTicketsPage.filterForm.resultExpressionText', { query: query }) : ''

  return (
    <StyledWrapper data-test='conditions-section'>
      <GroupHeader data-test='conditions-group-header'>{t('ticketProviderTicketsPage.filterForm.conditions')}</GroupHeader>
      <TopButtons>
        <AdvancedSwitcherWithInfo>
          <SwitchField
            name='advanced'
            checkboxLabel={t('ticketProviderTicketsPage.filterForm.advancedMode')}
          />
          {advanced && (
            <>
              <Tooltip id='advanced-tooltip' />
              <IconLink
                data-tooltip-content={t('ticketProviderTicketsPage.filterForm.advancedTooltip')}
                data-tooltip-id='advanced-tooltip'
                iconName='icon-help-16'
              />
            </>
          )}
        </AdvancedSwitcherWithInfo>

        {!advanced && (
          <IconLink
            iconName='icon-add'
            text={t('ticketProviderTicketsPage.filterForm.addNewCondition')}
            onClick={onConditionAdd}
          />
        )}
      </TopButtons>

      <AdvancedModePanel isVisible={advanced}>
        <QueryWrapper>
          <TextAreaField
            name='query'
            placeholder={t('common.typeHere')}
            minRowsTextArea={15}
          />
        </QueryWrapper>
      </AdvancedModePanel>

      <NormalModePanel isVisible={!advanced}>
        <Table width='100%'>
          <TableHead>
            <TableRow>
              <TableCell width={`${WIDTH_FIELD_CELL}px`}>{t('ticketProviderTicketsPage.filterForm.table.field')}</TableCell>
              <TableCell width={`${WIDTH_OPERATOR_CELL}px`}>{t('ticketProviderTicketsPage.filterForm.table.operator')}</TableCell>
              <TableCell width={`${WIDTH_VALUE_CELL}px`}>{t('ticketProviderTicketsPage.filterForm.table.value')}</TableCell>
              <TableCell width={`${WIDTH_LAST_CELL}px`} />
            </TableRow>
          </TableHead>
          <TableBody hoverable={false}>
            {conditions.fields.map((namePrefix, index) => {
              const condition = conditionsValues[index]
              const column = condition?.column || null
              const operator = condition?.equality || null
              const filterField = column ? filterFieldsMap[column] : null
              const tooltipId = 'remove-condition-tooltip-' + index

              return (
                <TableRow key={namePrefix}>
                  <TableCell>
                    <ColumnSelectField
                      id={`${namePrefix}.column`}
                      name={`${namePrefix}.column`}
                      filterFields={filterFields}
                      isLoading={isFetching}
                      onChange={(value) => onColumnChange(index, value)}
                    />
                  </TableCell>
                  <TableCell>
                    <OperatorSelectField
                      name={`${namePrefix}.equality`}
                      filterField={filterField}
                      isGlobal={isGlobal}
                    />
                  </TableCell>
                  <TableCell>
                    <ValueField
                      name={`${namePrefix}.value`}
                      filterField={filterField}
                      operator={operator}
                    />
                  </TableCell>
                  <TableCellWithActions>
                    {!hideDeleteButton && (
                      <>
                        <TableCellAction
                          alwaysShow
                          iconName='icon-remove-12'
                          onClick={() => onConditionDelete(index)}
                          tooltip={t('ticketProviderTicketsPage.filterForm.removeConditionTooltip')}
                          tooltipId={tooltipId}
                        />
                        <Tooltip id={tooltipId} />
                      </>
                    )}
                  </TableCellWithActions>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        <GroupHeader>{t('ticketProviderTicketsPage.filterForm.resultExpression')}</GroupHeader>
        <ResultExpression>{resultExpression}</ResultExpression>
      </NormalModePanel>
    </StyledWrapper>
  )
}

ConditionsSection.propTypes = {
  ticketProviderId: PropTypes.string.isRequired,
  partnerStatuses: PropTypes.array.isRequired,
}

export default ConditionsSection
